<template>
  <v-container class="pt-0 change-password">
    <v-row>
      <v-col class="pt-0">
        <h1 class="change-password__title">
          <sc-text :field="responseSuccess ? fields.SuccessTitle : fields.PageTitle" />
        </h1>
        <sc-rich-text :field="fields.PageDescription" v-if="!responseSuccess" />
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col :cols="$vuetify.breakpoint.mdAndUp ? 6 : 12">
        <v-form ref="changePasswordForm" class="change-password__form" v-show="!responseSuccess">
          <sc-rich-text class="change-password__form-description" :field="fields.FormDescription" />
          <p v-if="serverError" class="change-password__error">{{ serverError }}</p>
          <div class="change-password__label">
            <sc-text :field="fields.OldPasswordLabel" />
          </div>
          <v-text-field
            type="password"
            class="change-password__input"
            flat
            outlined
            solo
            v-model="oldPassword"
            :rules="oldPasswordRules"
          />
          <div class="change-password__label">
            <sc-text :field="fields.NewPasswordLabel" />
          </div>
          <v-text-field
            type="password"
            class="change-password__input"
            flat
            outlined
            solo
            v-model="newPassword"
            :rules="newPasswordRules"
          />
          <div class="change-password__label">
            <sc-text :field="fields.ConfirmPasswordLabel" />
          </div>
          <v-text-field
            type="password"
            class="change-password__input"
            flat
            outlined
            solo
            v-model="confirmNewPassword"
            :rules="confirmNewPasswordRules"
          />
          <div class="change-password__button-wrapper">
            <v-btn
              :block="$vuetify.breakpoint.xsOnly"
              large
              tile
              elevation="0"
              color="primary"
              class="change-password__button change-password__button--primary"
              @click="submitPasswordChange"
              :loading="responsePending"
              >{{ fields.ChangePasswordButtonLabel.value }}</v-btn
            >
            <v-btn
              :block="$vuetify.breakpoint.xsOnly"
              large
              tile
              outlined
              color="primary"
              class="change-password__button change-password__button--secondary"
              @click="$router.push('/')"
              >{{ fields.CancelButtonLabel.value }}</v-btn
            >
          </div>
        </v-form>
        <div class="change-password__success-container" v-show="responseSuccess">
          <img src="/img/success_check.svg" class="change-password__success-mark" />
          <v-expand-transition>
            <div v-show="responseSuccess">
              <h1 class="change-password__success-title">
                <sc-text :field="fields.SuccessTitle" />
              </h1>
              <sc-rich-text :field="fields.SuccessDescription" />
            </div>
          </v-expand-transition>
        </div>
        <sc-placeholder name="helpsection" v-if="!responseSuccess" class="change-password__help" :rendering="rendering" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Text, RichText, Placeholder, isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-vue';
import ProgressResponse from '../subComponents/ProgressResponse/ProgressResponse';
import { changePassword } from '../../api/index';
import { validation } from '../../lib/validation';

export default {
  name: 'ChangePassword',
  data() {
    return {
      responsePending: false,
      responseSuccess: false,
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      serverError: '',
      oldPasswordRules: [
        (v) => validation.valueRequired(v) || this.$t('validation-oldPasswordRequired'),
      ],
      newPasswordRules: [
        (v) => validation.passwordLength(v) || this.$t('validation-passwordAtLeast8Characters'),
        (v) =>
          validation.hasLowerOrUpperOrNumeral(v) || this.$t('validation-passwordLowerUpperNumber'),
        (v) => validation.hasLower(v) || this.$t('validation-passwordLower'),
        (v) => validation.hasUpper(v) || this.$t('validation-passwordUpper'),
        (v) => validation.anyNumerals(v) || this.$t('validation-passwordNumber'),
      ],
      confirmNewPasswordRules: [
        (v) =>
          validation.valuesMatch(v, this.newPassword) ||
          this.$t('validation-oldNewPasswordDoesNotMatch'),
      ],
    };
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    extractErrorMessage: function(response) {
      if(this.$logger && typeof(this.$logger)!=='undefined'){
        this.$logger.debug(`Error [${typeof response}]: %o`, response);
      }

      try {
        // eg. new password used too recently, incorrect old password
        const innerError = JSON.parse(response.response.data.errors[0].message);

        // API doesn't return unique error codes, so check the error cause text
        switch (innerError.errorCauses[0].errorSummary) {
          case 'Old Password is not correct':
            return this.$t('errors-E0000014-incorrectPassword');
          case 'Password has been used too recently':
            return innerError.errorCauses[0].errorSummary;
          case 'Password requirements were not met. Password requirements: at least 8 characters, a lowercase letter, an uppercase letter, a number, no parts of your username. Your password cannot be any of your last 4 passwords.':
            return this.$t('errors-E0000014-failedPasswordRequirements');
          default:
            return innerError.errorCauses[0].errorSummary;
        }
      } catch (ex) {
        if(this.$logger && typeof(this.$logger)!=='undefined'){
          this.$logger.error('ChangePassword - extractErrorMessage: ',ex);
        }
      }

      try {
        // eg. new password matches old password
        return response.data.errors[0].message;
      } catch (ex) {
        if(this.$logger && typeof(this.$logger)!=='undefined'){
          this.$logger.error('ChangePassword - extractErrorMessage: response.data.errors[0].message - ', ex);
        }
      }

      try {
        // eg. account suspended
        return JSON.parse(response.response.data.errors[0].message).errorSummary;
      } catch (ex) {
        if(this.$logger && typeof(this.$logger)!=='undefined'){
          this.$logger.error('ChangePassword - extractErrorMessage: JSON.parse(response.response.data.errors[0].message).errorSummary - ' ,ex);
        }
      }

      if (typeof response === 'string') {
        return response;
      }

      return this.fields.GeneralChangePasswordFailureMessage.value;
    },
    submitPasswordChange: function (event) {
      if (this.$refs.changePasswordForm.validate()) {
        this.responsePending = true;
        const response = changePassword(this.oldPassword, this.newPassword)
          .then(
            (response) => {
              this.responseSuccess = response.success;
              if (!this.responseSuccess) {
                this.serverError = this.extractErrorMessage(response);
              }
            },
            (errorResponse) => {
              this.serverError = this.extractErrorMessage(errorResponse);
            }
          )
          .finally(() => {
            this.responsePending = false;
          });
      }
    },
  },
  components: {
    ProgressResponse,
    ScText: Text,
    ScRichText: RichText,
    ScPlaceholder: Placeholder,
  },
  computed:{
    isSitecoreExperienceEditorOn() {
      return isExperienceEditorActive();
    },
  },
  mounted: function () {},
};
</script>
<style lang="scss" scoped>
.change-password {
  color: map-deep-get($colors, 'blue-text', 'base');

  &__success-container {
    text-align: center;
  }

  &__success-title {
    font-size: 28px;
    margin: 24px 0;
    font-weight: 500;
  }

  &__form-description {
    margin-bottom: 30px;
  }

  &__label {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    color: map-deep-get($colors, 'blue-title', 'base');
    margin-bottom: 7px;
  }

  &__title {
    margin-bottom: 24px;
    font-weight: 500;
  }
  &__button-wrapper {
    text-align: right;
    margin-top: 12px;
  }
  &__button {
    margin-left: 20px;

    @media #{map-get($display-breakpoints, 'xs-only')} {
      margin-left: 0;
      margin-bottom: 12px;
    }

    &--primary {
      @extend .btn-primary;
      float: right;
    }

    &--secondary {
      @extend .btn-secondary;
    }
  }
  &__error {
    margin-top: 20px;
    color: map-deep-get($colors, 'danger', 'base');
  }
  &__help {
    margin-top: 48px;
  }
}
</style>
<style lang="scss">
.change-password {
  &__input fieldset {
    border-color: currentColor !important;
  }
}
</style>
